<script lang="ts" setup>
import { ref, onMounted, computed, onBeforeUnmount, nextTick } from "vue";
import {
  useCreateDocumentSpaceMutation,
  CrmContact,
} from "@/graphql/operations";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Modal, ContactPicker } from "@/components";
import { useFlashMessage } from "../hooks/useFlashMessage";
import { useI18n } from "vue-i18n";
import {
  ProductUsageFeatureKey,
  trackFeatureUsage,
} from "@/util/ProductUsageAnalytics";

const { displayFlashMessage } = useFlashMessage();
const { t } = useI18n();

const store = useStore();
const router = useRouter();

const emit = defineEmits(["close", "success", "error"]);

const props = defineProps({
  redirect: {
    type: Boolean,
    default: true,
  },
  inheritClassicTheme: {
    type: Boolean,
    default: false,
  },
});

const withCRMIntegration = computed(() =>
  store.getters.user.hasCRMIntegration(),
);

const isCreatingDocumentSpace = ref<boolean>(false);
const inputName = ref();
const name = ref("");
const createError = ref(false);
const createErrorElement = ref<HTMLElement>();
const showCreateError = async () => {
  createError.value = true;
  await nextTick();
  createErrorElement.value?.scrollIntoView?.({ block: "nearest" });
  createErrorElement.value?.focus({ preventScroll: true });
  if (mounted) emit("error");
};
let mounted = true;
onBeforeUnmount(() => {
  mounted = false;
});

enum STEPS {
  SELECT_CONTACT = "selectContact",
  INSERT_NAME = "insertName",
}

const currentStep = ref<STEPS>(
  withCRMIntegration.value ? STEPS.SELECT_CONTACT : STEPS.INSERT_NAME,
);

onMounted(() => {
  if (!inputName.value) {
    return;
  }

  inputName.value.select();
});

const { executeMutation } = useCreateDocumentSpaceMutation();
const submit = async () => {
  if (name.value.length === 0 || isCreatingDocumentSpace.value) return;
  isCreatingDocumentSpace.value = true;
  createError.value = false;
  let completed = false;
  try {
    const result = await executeMutation({
      name: name.value,
      crmContactId: selectedContact.value?.id,
      configId: store.getters.user.mergedConfig?.defaultSpaceConfig,
    });
    if (!mounted) return;
    const spaceId =
      result.data?.createDocumentSpaceWithCrmContact?.result?.documentSpace?.id;
    if (result.error || !spaceId) {
      await showCreateError();
      return;
    }
    completed = true;
    trackFeatureUsage(ProductUsageFeatureKey.SharedSpaceCreated, {
      source: "legacy",
      sharedSpaceContext: "legacy_modal",
    });
    if (props.redirect) {
      displayFlashMessage(t("documentSpaces.successCreateNew"));
      router.push({ name: "DocumentSpace", params: { id: spaceId } });
    }
    emit("success", spaceId);
    emit("close");
  } catch {
    if (mounted) await showCreateError();
  } finally {
    // Embedded callers may keep this dialog mounted while refreshing their list.
    if (!completed) isCreatingDocumentSpace.value = false;
  }
};

const selectedContact = ref<CrmContact>();
const setSelectedContact = (contact: CrmContact, defaultName: string) => {
  if (isCreatingDocumentSpace.value) return;
  selectedContact.value = contact;
  name.value = defaultName;
};

const skip = () => {
  if (isCreatingDocumentSpace.value) return;
  createError.value = false;
  selectedContact.value = undefined;
  name.value = "";
  currentStep.value = STEPS.INSERT_NAME;
};

const goBack = () => {
  if (isCreatingDocumentSpace.value) return;
  createError.value = false;
  selectedContact.value = undefined;
  name.value = "";
  currentStep.value = STEPS.SELECT_CONTACT;
};
</script>

<template>
  <Modal
    v-if="currentStep === STEPS.INSERT_NAME"
    :class="{ 'classic-theme-light-only': !props.inheritClassicTheme }"
    :active="true"
    :close-disabled="isCreatingDocumentSpace"
    :aria-busy="isCreatingDocumentSpace"
    @close="emit('close')"
  >
    <template v-slot:header>
      <slot name="header"> </slot>
      {{ $t(`createDocumentSpaceModal.header.${currentStep}`) }}
    </template>

    <template v-slot:body>
      <div>
        <label class="label">
          {{ $t("createDocumentSpaceModal.name") }}
        </label>
        <form @submit.prevent="submit">
          <input
            class="input"
            type="text"
            v-model="name"
            ref="inputName"
            :disabled="isCreatingDocumentSpace"
          />
        </form>
      </div>
      <div
        v-if="createError"
        ref="createErrorElement"
        class="message error"
        role="alert"
        tabindex="-1"
      >
        {{ $t("error") }}
      </div>
    </template>
    <template v-slot:footer>
      <div class="buttons">
        <button
          v-if="withCRMIntegration"
          class="button secondary"
          :disabled="isCreatingDocumentSpace"
          @click="goBack"
        >
          {{ $t("createDocumentSpaceModal.previousStep") }}
        </button>
        <button
          v-else
          @click="emit('close')"
          class="button secondary"
          :disabled="isCreatingDocumentSpace"
        >
          {{ $t("cancel") }}
        </button>
        <button
          :class="{
            button: true,
            primary: true,
            disabled: name.length === 0 || isCreatingDocumentSpace,
          }"
          :disabled="isCreatingDocumentSpace || name.length === 0"
          @click="submit"
        >
          <span class="material-symbols-outlined">save</span>
          {{ $t("submit") }}
        </button>
      </div>
    </template>
  </Modal>

  <Modal
    v-else
    :active="true"
    :close-disabled="isCreatingDocumentSpace"
    :aria-busy="isCreatingDocumentSpace"
    @close="emit('close')"
    :class="{
      'classic-theme-light-only': !props.inheritClassicTheme,
      medium_large: true,
    }"
    :scrolling="true"
  >
    <template v-slot:header>
      <slot name="header"> </slot>
      {{ $t(`createDocumentSpaceModal.header.${currentStep}`) }}
    </template>

    <template v-slot:body>
      <div :inert="isCreatingDocumentSpace">
        <contact-picker
          :inherit-classic-theme="props.inheritClassicTheme"
          @change="setSelectedContact"
          @submit="submit"
          @close="emit('close')"
        />
      </div>
      <div
        v-if="createError"
        ref="createErrorElement"
        class="message error"
        role="alert"
        tabindex="-1"
      >
        {{ $t("error") }}
      </div>
    </template>

    <template v-slot:footer>
      <div class="buttons">
        <button
          class="button secondary"
          :disabled="isCreatingDocumentSpace"
          @click="skip"
        >
          <span class="material-symbols-outlined">save</span>
          {{ $t("createDocumentSpaceModal.skip") }}
        </button>
        <button
          :class="{
            button: true,
            primary: true,
            disabled: !selectedContact || isCreatingDocumentSpace,
          }"
          :disabled="isCreatingDocumentSpace || !selectedContact"
          @click="submit"
        >
          <span class="material-symbols-outlined">save</span>
          {{ $t("createDocumentSpaceModal.submit") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.message {
  box-sizing: border-box;
  margin-top: 1rem;
}

label {
  font-size: 0.9em;
  display: block;
  line-height: 2em;
}

.row {
  margin-bottom: 1em;
}

.contact_info {
  background-color: var(--classic-surface-hover, #fafafa);
  font-size: 0.9em;
  padding: 1em 0.5em;
}

.main_info {
  font-weight: 600;
}
</style>
